import React, { useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiSearch, FiX } from 'react-icons/fi';
import './ProjectSearchSelect.css';

/**
 * Reusable searchable project dropdown.
 * Props:
 *   projects   - array of { _id, projectId, title, client? }
 *   value      - selected project _id (string) or null for object mode
 *   onChange   - (project object | _id string) => void
 *   objectMode - if true, calls onChange with full project object; else with _id string
 *   placeholder- dropdown placeholder text
 *   allOption  - if true, shows "All Projects" as first option (value = '')
 *   label      - optional label shown above trigger
 *   className  - extra class on root
 */
export default function ProjectSearchSelect({
  projects = [],
  value,
  onChange,
  objectMode = false,
  placeholder = 'Select project…',
  allOption = false,
  label,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selected = objectMode
    ? (value || null)
    : projects.find(p => p._id === value) || null;

  const displayLabel = selected
    ? `${selected.projectId ? selected.projectId + ' – ' : ''}${selected.title}`
    : allOption ? 'All Projects' : placeholder;

  const filtered = projects.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.title?.toLowerCase().includes(q) ||
      p.projectId?.toLowerCase().includes(q) ||
      p.client?.name?.toLowerCase().includes(q)
    );
  });

  function select(p) {
    if (objectMode) onChange(p);
    else onChange(p ? p._id : '');
    setOpen(false);
    setSearch('');
  }

  const isActive = (p) => objectMode
    ? (value?._id || value) === p._id
    : value === p._id;

  return (
    <div className={`pss-root ${className}`} ref={ref}>
      {label && <span className="pss-label">{label}</span>}
      <div className={`pss-trigger ${open ? 'pss-open' : ''}`} onClick={() => setOpen(v => !v)}>
        <span className={`pss-val ${!selected && !allOption ? 'pss-placeholder' : ''}`}>
          {displayLabel}
        </span>
        <FiChevronDown className={`pss-chevron ${open ? 'pss-rotated' : ''}`} />
      </div>
      {open && (
        <div className="pss-dropdown">
          <div className="pss-search-row">
            <FiSearch size={12} className="pss-search-icon" />
            <input
              autoFocus
              className="pss-search-input"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            {search && (
              <button className="pss-clear" onClick={() => setSearch('')}>
                <FiX size={11} />
              </button>
            )}
          </div>
          <div className="pss-list">
            {allOption && (
              <div
                className={`pss-item ${!value ? 'pss-active' : ''}`}
                onClick={() => select(null)}
              >
                <span className="pss-item-title">All Projects</span>
                {!value && <span className="pss-check">✓</span>}
              </div>
            )}
            {filtered.map(p => (
              <div
                key={p._id}
                className={`pss-item ${isActive(p) ? 'pss-active' : ''}`}
                onClick={() => select(p)}
              >
                {p.projectId && <span className="pss-item-id">{p.projectId}</span>}
                <span className="pss-item-title">{p.title}</span>
                {p.client?.name && <span className="pss-item-client">{p.client.name}</span>}
                {isActive(p) && <span className="pss-check">✓</span>}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="pss-no-result">No projects found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
